// frontend/src/pages/CheckoutSucessoPage/CheckoutSucessoPage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Result, Spin, Space } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { sincronizarStatusAssinatura } from '../../services/assinaturaService';
import { trackMetaEvent } from '../../utils/metaPixel';
import logo from '../../assets/images/logo_horizontal_fundo_transparente.png';

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_SECONDS = 60;

const CheckoutSucessoPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const origemUpgrade = searchParams.get('origem') === 'upgrade';
  const { refreshUser, acessoPermitido } = useAuth();
  const [pollExhausted, setPollExhausted] = useState(false);
  const purchaseTrackedRef = useRef(false);
  // event_id do Purchase na Conversions API (payment_id do Asaas), para deduplicar.
  const purchaseEventIdRef = useRef(null);
  const [primeiroSyncFeito, setPrimeiroSyncFeito] = useState(false);

  const syncAcesso = async () => {
    try {
      const status = await sincronizarStatusAssinatura();
      if (status?.purchase_event_id) purchaseEventIdRef.current = status.purchase_event_id;
    } catch {
      /* sync é best-effort */
    }
    await refreshUser?.();
    setPrimeiroSyncFeito(true);
  };

  useEffect(() => {
    syncAcesso();
  }, []);

  useEffect(() => {
    if (acessoPermitido) return;
    const maxPolls = (POLL_MAX_SECONDS * 1000) / POLL_INTERVAL_MS;
    let count = 0;
    const interval = setInterval(async () => {
      count += 1;
      await syncAcesso();
      if (count >= maxPolls) {
        setPollExhausted(true);
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [acessoPermitido]);

  useEffect(() => {
    if (!acessoPermitido || !primeiroSyncFeito || purchaseTrackedRef.current) return;
    purchaseTrackedRef.current = true;
    try {
      const raw = sessionStorage.getItem('sa_pending_purchase');
      if (raw) {
        const data = JSON.parse(raw);
        trackMetaEvent('Purchase', {
          value: data.value,
          currency: data.currency || 'BRL',
          content_name: data.content_name,
        }, purchaseEventIdRef.current);
        sessionStorage.removeItem('sa_pending_purchase');
      } else if (!origemUpgrade) {
        // Sem dados do plano em sessionStorage (ex.: outra aba/sessão) — ainda assim
        // reporta a conversão para o Meta aprender o público, só sem valor.
        trackMetaEvent('Purchase', { currency: 'BRL' }, purchaseEventIdRef.current);
      }
    } catch (e) { /* tracking é best-effort, nunca deve travar o fluxo de acesso */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acessoPermitido, primeiroSyncFeito]);

  const aindaAguardando = !acessoPermitido;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'var(--color-background)',
      }}
    >
      <Card style={{ width: 440, textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <img
          src={logo}
          alt="Logo Saldo Ativo"
          style={{ width: 220, maxWidth: '80%', marginBottom: 24, display: 'block', margin: '0 auto 24px' }}
        />
        {aindaAguardando ? (
          <Result
            icon={<Spin size="large" />}
            title="Pagamento confirmado!"
            subTitle={
              pollExhausted
                ? 'O pagamento foi recebido. Você pode entrar no app — o acesso será validado ao carregar.'
                : origemUpgrade
                  ? 'Ativando seu novo plano... Aguarde alguns segundos.'
                  : 'Liberando seu acesso... Aguarde alguns segundos.'
            }
            extra={
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="link" onClick={() => syncAcesso()}>
                  Atualizar agora
                </Button>
                {pollExhausted && (
                  <Button type="primary" size="large" block onClick={() => navigate('/meu-desempenho')}>
                    Ir para o app
                  </Button>
                )}
              </Space>
            }
          />
        ) : (
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="Acesso liberado!"
            subTitle={
              origemUpgrade
                ? 'Seu plano foi atualizado. Você já pode usar os módulos contratados.'
                : 'Você já pode usar o Saldo Ativo.'
            }
            extra={[
              <Button
                type="primary"
                key="ir"
                size="large"
                block
                onClick={() => navigate(origemUpgrade ? '/meu-plano' : '/meu-desempenho')}
              >
                {origemUpgrade ? 'Ir para Meu Plano' : 'Ir para o app'}
              </Button>,
            ]}
          />
        )}
      </Card>
    </div>
  );
};

export default CheckoutSucessoPage;
